/**
===================================================
Module: frontend.src.services.api.ts
Description: API service for backend communication and authentication
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import {
  User,
  Project,
  Session,
  LoginRequest,
  LoginResponse,
  CreateProjectRequest,
  CreateSessionRequest,
  KeyframeData,
  RegisterRequest,
  RegisterResponse,
  PasswordResetRequest,
  PasswordResetResponse,
  PasswordResetConfirm,
  PasswordResetConfirmResponse,
} from "../types/index";

// Global upload constraints
export const UPLOAD_CONSTRAINTS = {
  MAX_FILE_SIZE_GB: parseInt(process.env.REACT_APP_MAX_FILE_SIZE_GB!),
  MAX_DURATION_SECONDS: parseInt(process.env.REACT_APP_MAX_DURATION_SECONDS!),
  ALLOWED_FILE_TYPES: [".mp4", "video/mp4"],
  MAX_NUMBER_OF_FILES: 1,
};

// Add new types for preprocessing
interface PreprocessingStep {
  type: "trim";
  params: {
    start_time: number;
    end_time: number;
  };
}

interface PreprocessingConfig {
  enabled: boolean;
  steps: PreprocessingStep[];
}

interface ProcessVideoRequest {
  job_id: string;
  video_source: string;
  output_map_path: string;
  keyframe_output_path: string;
  storage_type: string;
  notifier_config: any;
  preprocessing?: PreprocessingConfig;
}

interface JobStatusResponse {
  status: string;
  message: string;
  storage_type: string;
  notifier_type: string;
  video_metadata?: any;
  keyframes_extracted?: number;
  preprocessing_enabled?: boolean;
  error?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

class ApiService {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem("access_token");
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const config: RequestInit = {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
        ...options.headers,
      },
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      // Check if it's an authentication error (401)
      if (response.status === 401) {
        // Token is expired or invalid, clear it and redirect to login
        this.logout();
        window.location.href = "/login";
        throw new Error("Authentication failed. Please login again.");
      }

      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { detail: errorText };
      }
      throw new Error(errorData.detail || `API Error: ${response.status}`);
    }

    // Handle 204 No Content responses (like delete operations)
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // Use URLSearchParams for proper application/x-www-form-urlencoded format
    const formData = new URLSearchParams();
    // Lowercase since we use email as username.
    formData.append("username", credentials.username.toLowerCase());
    formData.append("password", credentials.password);

    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { detail: errorText };
      }
      throw new Error(errorData.detail || `Login failed: ${response.status}`);
    }

    const data = await response.json();
    this.token = data.access_token;
    localStorage.setItem("access_token", this.token!);
    return data;
  }

  async register(registrationData: RegisterRequest): Promise<RegisterResponse> {
    return this.request<RegisterResponse>("/auth/register", {
      method: "POST",
      body: JSON.stringify(registrationData),
    });
  }

  logout(): void {
    this.token = null;
    localStorage.removeItem("access_token");
  }

  // User endpoints
  async getCurrentUser(): Promise<User> {
    return this.request<User>("/users/me");
  }

  // Project endpoints
  async getProjects(): Promise<Project[]> {
    return this.request<Project[]>("/projects");
  }

  async createProject(project: CreateProjectRequest): Promise<Project> {
    return this.request<Project>("/projects", {
      method: "POST",
      body: JSON.stringify(project),
    });
  }

  async getProject(id: string): Promise<Project> {
    return this.request<Project>(`/projects/${id}`);
  }

  async updateProject(
    id: string,
    project: Partial<CreateProjectRequest>,
  ): Promise<Project> {
    return this.request<Project>(`/projects/${id}`, {
      method: "POST",
      body: JSON.stringify(project),
    });
  }

  async deleteProject(id: string): Promise<void> {
    return this.request<void>(`/projects/${id}`, {
      method: "DELETE",
    });
  }

  // Session endpoints
  async getSessions(projectId: string): Promise<Session[]> {
    return this.request<Session[]>(`/projects/${projectId}/sessions`);
  }

  async createSession(
    projectId: string,
    session: CreateSessionRequest,
  ): Promise<{
    id: string;
    upload_url: string;
    expires_in: number;
    status: string;
  }> {
    return this.request(`/projects/${projectId}/sessions`, {
      method: "POST",
      body: JSON.stringify(session),
    });
  }

  // Add new method for creating session with preprocessing
  async createSessionWithPreprocessing(
    projectId: string,
    session: CreateSessionRequest,
    preprocessing?: PreprocessingConfig,
  ): Promise<{
    id: string;
    upload_url: string;
    expires_in: number;
    status: string;
  }> {
    const requestBody = {
      ...session,
      video_filename: "video.mp4",
      preprocessing,
    };

    return this.request(`/projects/${projectId}/sessions`, {
      method: "POST",
      body: JSON.stringify(requestBody),
    });
  }

  async getSession(id: string): Promise<Session> {
    return this.request<Session>(`/sessions/${id}`);
  }

  async updateSession(
    id: string,
    session: Partial<CreateSessionRequest>,
  ): Promise<Session> {
    return this.request<Session>(`/sessions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(session),
    });
  }

  // Add new method for updating session name and description
  async updateSessionDetails(
    sessionId: string,
    updates: { name: string; description: string },
  ): Promise<Session> {
    return this.request<Session>(`/sessions/${sessionId}/update`, {
      method: "POST",
      body: JSON.stringify(updates),
    });
  }

  async getSessionInputVideo(sessionId: string): Promise<{
    download_url: string;
    expires_in: number;
    filename: string;
  }> {
    return this.request(`/sessions/${sessionId}/input_video`);
  }

  async deleteSession(id: string): Promise<void> {
    return this.request<void>(`/sessions/${id}`, {
      method: "DELETE",
    });
  }

  async getUploadUrl(sessionId: string): Promise<{ upload_url: string }> {
    return this.request<{ upload_url: string }>(
      `/sessions/${sessionId}/upload-url`,
    );
  }

  async startProcessing(sessionId: string): Promise<void> {
    return this.request<void>(`/sessions/${sessionId}/start-processing`, {
      method: "POST",
    });
  }

  async getSessionResults(
    sessionId: string,
    keyframeCenter: number = 0,
    keyframeRange: number = 20,
  ): Promise<{
    map_url?: string;
    keyframes?: Record<string, string>;
    keyframes_array?: KeyframeData[];
    keyframe_metadata?: {
      total_keyframes: number;
      current_range: { start: number; end: number; center: number };
    };
  }> {
    const params = new URLSearchParams({
      keyframe_center: keyframeCenter.toString(),
      keyframe_range: keyframeRange.toString(),
    });

    return this.request<{
      map_url?: string;
      keyframes?: Record<string, string>;
      keyframes_array?: KeyframeData[];
      keyframe_metadata?: any;
    }>(`/sessions/${sessionId}/results?${params}`);
  }

  async uploadVideoFile(file: File, uploadUrl: string): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": "video/mp4",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Upload failed: ${response.status} ${response.statusText}`,
      );
    }
  }

  async notifyUploadComplete(sessionId: string): Promise<void> {
    return this.request<void>(`/sessions/${sessionId}/upload-complete`, {
      method: "POST",
    });
  }

  async uploadFileWithProgress(
    file: File,
    uploadUrl: string,
    onProgress: (progress: number) => void,
  ): Promise<void> {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        const progress = (event.loaded / event.total) * 100;
        onProgress(progress);
      }
    });

    return new Promise((resolve, reject) => {
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed due to network error"));
      });

      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("Content-Type", "video/mp4");
      xhr.send(file);
    });
  }

  async exportTrajectory(
    sessionId: string,
    format: "gpx" | "kml" | "csv",
  ): Promise<void> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/sessions/${sessionId}/export/trajectory?format=${format}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Export failed");
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get("Content-Disposition");

      let filename = `trajectory_export.${format}`; // fallback

      if (contentDisposition) {
        const quotedMatch = contentDisposition.match(
          /filename\*=UTF-8''([^"]+)/,
        );

        if (quotedMatch && quotedMatch[1]) {
          filename = decodeURIComponent(quotedMatch[1]);
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw error;
    }
  }

  async exportKeyframe(
    sessionId: string,
    filename: string,
    frameType: "2d" | "3d",
  ): Promise<void> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/sessions/${sessionId}/export/keyframe/${encodeURIComponent(filename)}?frame_type=${frameType}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
            Accept: "image/jpeg",
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();

        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { detail: errorText || "Keyframe export failed" };
        }

        throw new Error(errorData.detail || "Keyframe export failed");
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get("Content-Disposition");
      let exportFilename = `keyframe_${filename}`; // fallback

      if (contentDisposition) {
        const quotedMatch = contentDisposition.match(
          /filename\*=UTF-8''([^"]+)/,
        );

        if (quotedMatch && quotedMatch[1]) {
          exportFilename = decodeURIComponent(quotedMatch[1]);
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw error;
    }
  }

  async forgotPassword(email: string): Promise<PasswordResetResponse> {
    return this.request<PasswordResetResponse>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(
    resetData: PasswordResetConfirm,
  ): Promise<PasswordResetConfirmResponse> {
    return this.request<PasswordResetConfirmResponse>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify(resetData),
    });
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  // Add method to check if token is valid (not expired)
  async validateToken(): Promise<boolean> {
    if (!this.token) {
      return false;
    }

    try {
      // Try to get current user to validate token
      await this.getCurrentUser();
      return true;
    } catch (error) {
      // If we get a 401, token is expired
      if (
        error instanceof Error &&
        error.message.includes("Authentication failed")
      ) {
        return false;
      }
      return true;
    }
  }

  async deleteSessions(sessionIds: string[]): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/sessions/bulk-delete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
      },
      body: JSON.stringify({ session_ids: sessionIds }),
    });

    if (!response.ok) {
      throw new Error("Failed to delete sessions");
    }
  }
}

export const apiService = new ApiService();
